<template>
  <div
    class="min-h-screen w-full flex flex-col bg-gradient-to-br from-spicy-mustard-300 to-mocha-mousse-300"
  >
    <!-- Header Bar -->
    <HeaderBar :logo="logo">
      <template #account>
        <LoginDropdown />
      </template>
    </HeaderBar>

    <!-- Main Content -->
    <div class="flex-1 px-3 md:px-6 py-5 md:py-10">
      <div class="max-w-7xl mx-auto w-full">
        <!-- Header -->
        <div class="mb-10 text-center">
          <h1 class="text-4xl font-bold text-mocha-mousse-900">
            Admin Dashboard
          </h1>
          <p class="mt-3 text-mocha-mousse-700 text-lg">
            System overview and statistics
          </p>
        </div>

        <!-- Overview Stats -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <div
            class="bg-white rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer p-2 md:p-6 border-2 border-transparent border-t-mocha-mousse-500"
            @click="showUserList('total')"
          >
            <div class="flex flex-col items-center text-center">
              <Icon
                icon="mdi:account-group"
                class="h-12 w-12 text-mocha-mousse-500 mb-3"
              />
              <div class="text-4xl font-bold text-gray-900">
                {{ overviewStats.total_users || 0 }}
              </div>
              <div class="text-sm text-gray-600 mt-1">Total Users</div>
            </div>
          </div>

          <div
            class="bg-white rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer p-2 md:p-6 border-2 border-transparent border-t-green-500"
            @click="showUserList('active_today')"
          >
            <div class="flex flex-col items-center text-center">
              <Icon
                icon="mdi:account-check"
                class="h-12 w-12 text-green-500 mb-3"
              />
              <div class="text-4xl font-bold text-gray-900">
                {{ overviewStats.active_today || 0 }}
              </div>
              <div class="text-sm text-gray-600 mt-1">Active Today</div>
            </div>
          </div>

          <div
            class="bg-white rounded-xl shadow-sm p-2 md:p-6 border-2 border-transparent border-t-blue-500"
          >
            <div class="flex flex-col items-center text-center">
              <Icon icon="mdi:server" class="h-12 w-12 text-blue-500 mb-3" />
              <div class="text-4xl font-bold text-gray-900">
                {{ overviewStats.total_requests || 0 }}
              </div>
              <div class="text-sm text-gray-600 mt-1">Requests Today</div>
            </div>
          </div>

          <div
            class="bg-white rounded-xl shadow-sm p-2 md:p-6 border-2 border-transparent border-t-red-500"
          >
            <div class="flex flex-col items-center text-center">
              <Icon
                icon="mdi:alert-circle"
                class="h-12 w-12 text-red-500 mb-3"
              />
              <div class="text-4xl font-bold text-gray-900">
                {{ overviewStats.recent_errors || 0 }}
              </div>
              <div class="text-sm text-gray-600 mt-1">Recent Errors (7d)</div>
            </div>
          </div>
        </div>

        <!-- Charts Section -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <!-- User Statistics Chart -->
          <div class="bg-white rounded-xl shadow-sm p-2 md:p-6">
            <h2
              class="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2"
            >
              <Icon icon="mdi:account-group" class="text-mocha-mousse-600" />
              User Statistics
            </h2>
            <div class="space-y-4">
              <div
                class="flex justify-between items-center py-2 border-b border-gray-100"
              >
                <span class="text-gray-600">Total Users</span>
                <span class="font-semibold text-gray-900">{{
                  userStats.total_users || 0
                }}</span>
              </div>
              <div
                class="flex justify-between items-center py-2 border-b border-gray-100"
              >
                <span class="text-gray-600">Active Today</span>
                <span class="font-semibold text-green-600">{{
                  userStats.active_users_today || 0
                }}</span>
              </div>
              <div
                class="flex justify-between items-center py-2 border-b border-gray-100"
              >
                <span class="text-gray-600">New This Month</span>
                <span class="font-semibold text-blue-600">{{
                  userStats.new_users_this_month || 0
                }}</span>
              </div>
              <div class="flex justify-between items-center py-2">
                <span class="text-gray-600">Admin Users</span>
                <span class="font-semibold text-purple-600">{{
                  userStats.admin_users || 0
                }}</span>
              </div>
            </div>
          </div>

          <!-- Service Statistics Chart -->
          <div class="bg-white rounded-xl shadow-sm p-2 md:p-6">
            <h2
              class="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2"
            >
              <Icon icon="mdi:chart-line" class="text-mocha-mousse-600" />
              Service Statistics
            </h2>
            <div class="space-y-4">
              <div
                class="flex justify-between items-center py-2 border-b border-gray-100"
              >
                <span class="text-gray-600">Total Requests</span>
                <span class="font-semibold text-gray-900">{{
                  serviceStats.total_requests || 0
                }}</span>
              </div>
              <div
                class="flex justify-between items-center py-2 border-b border-gray-100"
              >
                <span class="text-gray-600">Requests (24h)</span>
                <span class="font-semibold text-blue-600">{{
                  serviceStats.requests_last_24h || 0
                }}</span>
              </div>
              <div
                class="flex justify-between items-center py-2 border-b border-gray-100"
              >
                <span class="text-gray-600">Error Rate</span>
                <span class="font-semibold" :class="errorRateClass">
                  {{ serviceStats.error_rate || 0 }}%
                </span>
              </div>
              <div class="flex justify-between items-center py-2">
                <span class="text-gray-600">Avg Response Time</span>
                <span class="font-semibold text-gray-900"
                  >{{ serviceStats.average_response_time || 0 }}ms</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Concurrent Users -->
        <div class="bg-white rounded-xl shadow-sm p-2 md:p-6 mb-10">
          <h2
            class="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2"
          >
            <Icon
              icon="mdi:account-group-outline"
              class="text-mocha-mousse-600"
            />
            Concurrent Users
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <!-- Current Users - Clickable -->
            <div
              class="bg-mocha-mousse-50 rounded-lg hover:shadow-md transition-all cursor-pointer group p-2 md:p-6 border-2 border-transparent hover:border-blue-300"
              @click="showUserList('concurrent')"
            >
              <div class="flex flex-col items-center text-center">
                <Icon
                  icon="mdi:account-group"
                  class="h-10 w-10 text-blue-500 mb-3 group-hover:scale-110 transition-transform"
                />
                <div class="text-5xl font-bold text-blue-600">
                  {{ concurrentStats.current_concurrent || 0 }}
                </div>
                <div class="text-gray-600 text-sm mt-2">Current Users</div>
              </div>
            </div>

            <!-- Peak Today -->
            <div
              class="bg-green-50 rounded-lg p-2 md:p-6 border-2 border-transparent"
            >
              <div class="flex flex-col items-center text-center">
                <Icon
                  icon="mdi:chart-line"
                  class="h-10 w-10 text-green-500 mb-3"
                />
                <div class="text-5xl font-bold text-green-600">
                  {{ concurrentStats.peak_concurrent_today || 0 }}
                </div>
                <div class="text-gray-600 text-sm mt-2">Peak Today</div>
              </div>
            </div>

            <!-- Average Today -->
            <div
              class="bg-purple-50 rounded-lg p-2 md:p-6 border-2 border-transparent"
            >
              <div class="flex flex-col items-center text-center">
                <Icon
                  icon="mdi:chart-bar"
                  class="h-10 w-10 text-purple-500 mb-3"
                />
                <div class="text-5xl font-bold text-purple-600">
                  {{ concurrentStats.average_concurrent_today || 0 }}
                </div>
                <div class="text-gray-600 text-sm mt-2">
                  Avg Today (users/5min)
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation to Logs -->
        <div class="bg-white rounded-xl shadow-sm p-2 md:p-6">
          <div
            class="flex flex-col sm:flex-row items-center justify-between gap-4"
          >
            <div class="flex items-center gap-4">
              <div class="bg-mocha-mousse-100 p-4 rounded-full">
                <Icon
                  icon="mdi:file-document-multiple"
                  class="h-8 w-8 text-mocha-mousse-600"
                />
              </div>
              <div>
                <h2 class="text-xl font-semibold text-gray-900">System Logs</h2>
                <p class="text-gray-600">
                  View detailed system logs and filtering options
                </p>
              </div>
            </div>
            <router-link
              :to="{ name: 'AdminLogs' }"
              class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-mocha-mousse-600 hover:bg-mocha-mousse-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mocha-mousse-500 transition-colors"
            >
              <Icon icon="mdi:arrow-right" class="mr-2 h-5 w-5" />
              View Logs
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- User List Modal -->
    <UserListModal
      :is-open="isUserModalOpen"
      :user-list="userList"
      :modal-title="userModalTitle"
      @close="closeUserModal"
      @refresh="refreshUserModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Icon } from "@iconify/vue";
import api from "@/services/api";
import UserListModal from "@/components/UserListModal.vue";
import HeaderBar from "@/components/HeaderBar.vue";
import LoginDropdown from "@/components/LoginDropdown.vue";

const overviewStats = ref({});
const userStats = ref({});
const serviceStats = ref({});
const concurrentStats = ref({});
const loading = ref(true);
const logo = ref("");

// User list modal state
const isUserModalOpen = ref(false);
const userList = ref([]);
const userModalTitle = ref("");
const usersLoading = ref(false);

const errorRateClass = computed(() => {
  const rate = serviceStats.value.error_rate || 0;
  if (rate > 10) return "text-red-600";
  if (rate > 5) return "text-yellow-600";
  return "text-green-600";
});

const fetchDashboardData = async () => {
  try {
    loading.value = true;

    // Fetch all dashboard data in parallel
    const [overviewRes, userRes, serviceRes, concurrentRes] = await Promise.all(
      [
        api.getDashboardStats("overview"),
        api.getDashboardStats("users"),
        api.getDashboardStats("service"),
        api.getDashboardStats("concurrent"),
      ],
    );

    overviewStats.value = overviewRes.data.message || {};
    userStats.value = userRes.data.message || {};
    serviceStats.value = serviceRes.data.message || {};
    concurrentStats.value = concurrentRes.data.message || {};
  } catch (error) {
    console.error("Failed to fetch dashboard data:", error);
  } finally {
    loading.value = false;
  }
};

const showUserList = async (type) => {
  usersLoading.value = true;
  isUserModalOpen.value = true;

  try {
    let response;
    switch (type) {
      case "total":
        userModalTitle.value = "All Users";
        response = await api.getAdminUsers();
        break;
      case "active_today":
        userModalTitle.value = "Active Users Today";
        response = await api.getActiveUsersToday();
        break;
      case "concurrent":
        userModalTitle.value = "Currently Active Users";
        response = await api.getConcurrentUsers();
        break;
      default:
        response = await api.getAdminUsers();
        userModalTitle.value = "User List";
    }

    userList.value = response.data.message.users || [];
  } catch (error) {
    console.error("Failed to fetch user list:", error);
    userList.value = [];
  } finally {
    usersLoading.value = false;
  }
};

const closeUserModal = () => {
  isUserModalOpen.value = false;
  userList.value = [];
  userModalTitle.value = "";
};

const refreshUserModal = () => {
  // Re-fetch the currently displayed user list
  if (userModalTitle.value.includes("All Users")) {
    showUserList("total");
  } else if (userModalTitle.value.includes("Active Users Today")) {
    showUserList("active_today");
  } else if (userModalTitle.value.includes("Currently Active Users")) {
    showUserList("concurrent");
  }
};

onMounted(() => {
  fetchDashboardData();
  // Refresh data every 5 minutes
  setInterval(fetchDashboardData, 5 * 60 * 1000);
  logo.value = `${process.env.BASE_URL}images/enodo-logo.png`;
});
</script>
